import Icon from '@/components/snippets/icon/icon'
import clsx from 'clsx'
import { useState, useEffect, useMemo } from 'preact/hooks'
import { globalState } from '@/components/helpers/utils'
import { generateSrcSet, getLowQualityPlaceholder } from './ResultImage'
import { Price } from 'libs/searchspring/components/Price'
import { Promoline } from 'libs/searchspring/components/Promoline'
import { render } from 'preact'
import { hasB2BDiscount } from '@/components/custom-elements/b2b-price'
import { AfterpayPrice } from 'libs/searchspring/components/AfterpayPrice'

const IMAGE_WIDTHS = [320, 480, 560, 640, 720, 960]

export const isGiftCard = (result) => {
  if (!result) return false

  const isGiftCardByProperty =
    result.is_giftcard === 'true' ||
    result.is_giftcard === true ||
    result.is_giftcard === 1 ||
    result.template_suffix === 'gift-card' ||
    result.product_type === 'Gift Card' ||
    result.product_type === 'Gift Cards' ||
    result.type === 'Gift Card' ||
    result.type === 'Gift Cards'

  const handle = result.handle || ''
  const name = result.name || result.title || ''
  const isGiftCardByName =
    handle.toLowerCase().includes('gift-card') ||
    handle.toLowerCase().includes('giftcard') ||
    name.toLowerCase().includes('gift card') ||
    name.toLowerCase().includes('gift-card')

  return isGiftCardByProperty || isGiftCardByName
}

const getStockStatus = (variant, stockLevels) => {
  if (!variant) return { inStock: false, text: '' }

  if (variant.inventory_management !== 'shopify' && variant.inventory_management !== 'Shopify') {
    return {
      inStock: true,
      text: '',
      warning: false,
      stockLevel: null,
      text_color: ''
    }
  }

  const quantity = variant.inventory_quantity

  const zeroStockLevel = stockLevels.find(stock => stock.max_quantity === 0)

  if (quantity <= 0 && zeroStockLevel) {
    return {
      inStock: false,
      text: zeroStockLevel.message,
      warning: true,
      stockLevel: quantity,
      textColour: zeroStockLevel.message_colour
    }
  }

  if (quantity <= 0) {
    return { inStock: false, text: 'Out of stock', warning: true, stockLevel: quantity, textColour: '#b3b3b3' }
  }

  for (const stock of stockLevels) {
    if (quantity <= stock.max_quantity) {
      return {
        inStock: true,
        text: stock.message,
        warning: true,
        stockLevel: quantity,
        textColour: stock.message_colour
      }
    }
  }

  return {
    inStock: true,
    text: '',
    warning: false,
    stockLevel: quantity,
    text_color: ''
  }
}

const ModalContent = ({ result, modalId, firstPromoline, secondPromoline, onClose }) => {
  const variants = Array.isArray(result.variants) ? result.variants : []
  const [variant] = variants
  const [selectedSize, setSelectedSize] = useState('')
  const [loading, setLoading] = useState(false)
  const [showSizeError, setShowSizeError] = useState(false)

  useEffect(() => {
    setSelectedSize('')
    setLoading(false)
    setShowSizeError(false)
  }, [result?.id, result?.uid])

  const isB2BCustomer = window.globals?.customer?.b2b
  const b2bPricing = window.globals?.customer?.b2bPricing
  const productId = result.uid || result.id?.toString?.() || result.id
  const b2bData = isB2BCustomer && b2bPricing ? b2bPricing[productId] : null
  const hasQuickB2bCheck = isB2BCustomer && b2bData && b2bData.price
  const [b2bPriceCheck, setB2bPriceCheck] = useState(hasQuickB2bCheck)

  useEffect(() => {
    if (window.lazyLoadInstance) {
      setTimeout(() => {
        window.lazyLoadInstance.update()
      }, 0)
    }
  }, [result?.images, result?.id, result?.uid])

  useEffect(() => {
    if (isB2BCustomer && variant?.id) {
      hasB2BDiscount(variant.id).then(hasDiscount => {
        setB2bPriceCheck(hasDiscount)
      }).catch(() => {
        setB2bPriceCheck(false)
      })
    } else {
      setB2bPriceCheck(false)
    }
  }, [isB2BCustomer, variant?.id])

  const finalPrice = variant?.price
  const finalCompareAtPrice = variant?.compare_at_price

  const selectedVariantPrice = useMemo(() => {
    if (!selectedSize) {
      return variant?.price
    }
    const sizeToMatch = selectedSize.split('/')[0].trim()
    const targetVariant = variants.find(v => v.option2 === sizeToMatch || v.title === selectedSize)
    return targetVariant?.price || variant?.price
  }, [selectedSize, variants, variant?.price])

  const productPriceMin = useMemo(() => {
    if (!variants || variants.length === 0) return 0
    const prices = variants.map(v => {
      const price = typeof v.price === 'string' ? parseFloat(v.price) : (v.price || 0)
      return price > 0 ? Math.round(price * 100) : 0
    }).filter(price => price > 0)
    return prices.length > 0 ? Math.min(...prices) : 0
  }, [variants])

  const handleAddToCart = async () => {
    if (!selectedSize) {
      return
    }

    if (loading) {
      return
    }

    let targetVariant = variants.find(v => {
      if (v.option2 === selectedSize || v.option1 === selectedSize || v.title === selectedSize) {
        return true
      }
      if (String(v.id) === String(selectedSize)) {
        return true
      }
      return false
    })

    if (!targetVariant) {
      const sizeToMatch = selectedSize.split('/')[0].trim()
      targetVariant = variants.find(v => {
        const option2Match = v.option2 && v.option2.includes(sizeToMatch)
        const option1Match = v.option1 && v.option1.includes(sizeToMatch)
        const titleMatch = v.title && v.title.includes(sizeToMatch)
        return option2Match || option1Match || titleMatch
      })
    }

    if (!targetVariant) {
      return
    }

    if (!window.Cart) {
      return
    }

    const variantId = typeof targetVariant.id === 'string' ? parseInt(targetVariant.id, 10) : targetVariant.id

    const properties = {}
    const hasUniversalSizing = variants.length > 0 && variants.some(v => {
      if (!v || !v.id) {
        return false
      }
      const hasMeta = !!(v.mfield_products_uk_size || v.mfield_products_us_size || v.mfield_products_eu_sizing ||
             v.mfield_products_cm_sizing ||
             v.metafield_products_uk_size || v.metafield_products_us_size || v.metafield_products_eu_sizing ||
             v.metafield_products_cm_sizing ||
             (v.metafields && Array.isArray(v.metafields) && v.metafields.some(m =>
               m.namespace === 'products' && (m.key === 'uk_size' || m.key === 'us_size' || m.key === 'eu_sizing' || m.key === 'cm_sizing')
             )))
      return hasMeta
    })

    if (hasUniversalSizing && targetVariant) {
      const getMeta = (key) => {
        return targetVariant[`mfield_products_${key}`] ||
               targetVariant[`metafield_products_${key}`] ||
               (targetVariant.metafields && Array.isArray(targetVariant.metafields) &&
                targetVariant.metafields.find(m => m.namespace === 'products' && m.key === key)?.value) ||
               null
      }

      const parseSizeValue = (sizeValue, sizeType) => {
        if (!sizeValue) return sizeValue
        if (!sizeValue.includes('-')) {
          return sizeValue
        }

        const regions = sizeValue.split('-').map(r => r.trim()).filter(r => r)

        for (const region of regions) {
          const cleanRegion = region.trim()

          if (sizeType === 'UK' && cleanRegion.startsWith('UK ')) {
            const ukContent = cleanRegion.replace('UK ', '').trim()
            return ukContent
          }

          if (sizeType === 'US' && cleanRegion.startsWith('US ')) {
            const usContent = cleanRegion.replace('US ', '').trim()
            return usContent
          }

          if (sizeType === 'EU' && cleanRegion.startsWith('EURO ')) {
            return cleanRegion.replace('EURO ', '').trim()
          }

          if (sizeType === 'CM' && cleanRegion.startsWith('CM ')) {
            return cleanRegion.replace('CM ', '').trim()
          }
        }

        return sizeValue
      }

      const productKey = result.uid || result.id || result.handle
      const sizeTypeStorageKey = `__quickAddSizeType_${productKey}`
      const hasKidsTag = result.tags && Array.isArray(result.tags) && result.tags.includes('Age:Kids')
      const defaultSizeRange = hasKidsTag ? 'UK' : (result.mfield_utopia_default_size_range || result.metafield_utopia_default_size_range || 'US')
      const normalizedDefaultSize = defaultSizeRange === 'EURO' ? 'EU' : defaultSizeRange
      const selectedSizeType = window[sizeTypeStorageKey] || normalizedDefaultSize

      let rawSizeValue = null
      let parsedSizeValue = ''
      let sizePropertyValue = ''

      switch (selectedSizeType) {
        case 'UK':
          rawSizeValue = getMeta('uk_size')
          parsedSizeValue = parseSizeValue(rawSizeValue, 'UK')
          sizePropertyValue = `UK ${parsedSizeValue}`
          break
        case 'EU':
          rawSizeValue = getMeta('eu_sizing')
          parsedSizeValue = parseSizeValue(rawSizeValue, 'EU')
          sizePropertyValue = `EURO ${parsedSizeValue}`
          break
        case 'CM':
          rawSizeValue = getMeta('cm_sizing')
          parsedSizeValue = parseSizeValue(rawSizeValue, 'CM')
          sizePropertyValue = `CM ${parsedSizeValue}`
          break
        case 'US':
        default:
          rawSizeValue = getMeta('us_size')
          parsedSizeValue = parseSizeValue(rawSizeValue, 'US')
          sizePropertyValue = `US ${parsedSizeValue}`
          break
      }

      if (sizePropertyValue) {
        properties.Size = sizePropertyValue
      }
    }

    setLoading(true)
    try {
      await window.Cart.addLineItems({
        id: variantId,
        quantity: 1,
        properties: Object.keys(properties).length > 0 ? properties : undefined
      })
      setSelectedSize('')
      setLoading(false)
      setTimeout(() => {
        onClose()
      }, 100)
    } catch (error) {
      console.error('Error adding item to cart:', error)
      setLoading(false)
    }
  }

  return (
    <>
      {/* Modal Header */}
      <div className="shrink-0 flex items-center justify-between sticky top-0 h-[52px] md:h-[60px] pl-1 md:pl-2 pr-0.625 md:pr-1 border-b border-neutral-medium z-20">
        <h2 className="h6 font-semibold">Quick Add</h2>
        <button
          className="btn btn-primary btn-text btn-square w-[44px] link-fade"
          data-modal-parent={modalId}
          data-modal-role="close"
          aria-label="Close quick add modal"
          onClick={onClose}
        >
          <div className="flex size-[12px] children:size-full">
            <Icon id="close" />
          </div>
        </button>
      </div>
      {/* Modal Inner */}
      <div className="grow flex flex-col gap-y-1.5 p-1 md:p-2 overflow-y-scroll no-scrollbar overscroll-none children:shrink-0">
        {/* Product Details */}
        <div className="flex gap-x-1.25 md:gap-x-1.5">
          <div className="w-[100px] aspect-4/5 overflow-hidden bg-neutral-light">
            <img
              className="lazy size-full object-cover"
              src={getLowQualityPlaceholder(result.images[0].src)}
              data-src={result.images[0].src}
              data-srcset={generateSrcSet(result.images[0].src, IMAGE_WIDTHS)}
              data-sizes="50vw"
              alt={result.images[0].alt || result.title || 'Product image'}
            />
          </div>
          <div className="grow flex flex-col gap-y-0.5 lg:gap-y-1">
            <div className="flex lg-max:flex-col lg:justify-between gap-0.5">
              <h3 className="h6 font-semibold" dangerouslySetInnerHTML={{ __html: result.mfield_custom_web_friendly_title || result.metafield_custom_web_friendly_title || result.metafields?.custom?.web_friendly_title || result.title }}></h3>
              {isB2BCustomer ? (
                <b2b-price
                  class="group/price flex gap-x-0.25"
                  data-variant-id={variants[0]?.id}>
                  <span data-original-price class="hidden group-data-[sale=true]/price:block line-through paragraph-m font-semibold"></span>
                  <span data-b2b-price class="group-data-[sale=true]/price:text-brand-red paragraph-m font-semibold"></span>
                </b2b-price>
              ) : (
                <Price price={finalPrice} compareAtPrice={finalCompareAtPrice} containerClass="flex gap-x-0.25" priceClass="paragraph-m font-semibold" />
              )}
            </div>
            {(b2bPriceCheck || firstPromoline || secondPromoline) && (
              <div className="flex flex-col gap-0.25">
                {b2bPriceCheck && (
                  <Promoline promoline={{
                    title: 'Wholesale Price',
                    text_colour: '#9C0000',
                    background_colour: '#FCEBEB'
                  }} promolineSize="medium" />
                )}
                {firstPromoline && <Promoline promoline={firstPromoline} promolineLocation="PLP" promolineSize="medium" />}
                {!b2bPriceCheck && secondPromoline && <Promoline promoline={secondPromoline} promolineLocation="PLP" promolineSize="medium" />}
              </div>
            )}
            <a href={result.url} className="btn btn-primary btn-text btn-s w-fit">
              <span>View Product Details</span>
            </a>
          </div>
        </div>
        <hr className="border-neutral-light" />
        {/* Size Selector */}
        <SizeSelector
          result={result}
          variants={variants}
          setSelectedSize={(size) => {
            setSelectedSize(size)
            setShowSizeError(false)
          }}
          selectedSize={selectedSize}
          sizeGuide={false}
          showSizeError={showSizeError}
          onSizeTypeChange={() => {
          }}
        />
      </div>
      {/* Action Buttons */}
      <div className="shrink-0 flex flex-col gap-y-1 md:gap-y-1.5 pl-1 md:pl-2 pr-0.625 md:pr-1 py-1 md:py-1.5 border-t border-neutral-light">
        <button
          className={clsx(
            'shrink-0 btn btn-fill btn-l w-full',
            !selectedSize ? 'btn-accent disabled' : 'btn-primary',
            loading && ' disabled'
          )}
          type="button"
          onClick={() => {
            if (selectedSize) {
              handleAddToCart()
            } else {
              setShowSizeError(true)
              const selectSizeModal = document.querySelector(`x-modal[data-id="select-size-text-${result.id || result.uid}"]`)
              if (selectSizeModal && typeof selectSizeModal.handleShow === 'function') {
                selectSizeModal.handleShow()
              }
            }
          }}
          aria-label="Add To Bag"
        >
          {!selectedSize ? 'Select Your Size' : 'Add To Bag'}
        </button>
      </div>
      {/* Product Messaging */}
      <div className="flex items-stretch gap-px w-full h-fit border-t border-neutral-light bg-neutral-light">
        {globalState?.product?.messaging?.[0]?.afterpay && (
          <AfterpayPrice
            price={selectedVariantPrice}
            messagingHTML={globalState.product.messaging[0].afterpay}
            containerClass="grow richtext paragraph-b text-center px-margin py-0.875 md:py-1.125 bg-brand-white"
          />
        )}
        <free-shipping-usp
          data-product-id={String(productId || '')}
          data-product-price-min={String(productPriceMin)}
          data-text-class="richtext paragraph-b text-center"
          class="grow px-margin py-0.875 md:py-1.125 bg-brand-white"
        />
      </div>
    </>
  )
}

const SizeSelector = ({ result, variants, setSelectedSize, selectedSize, sizeGuide, showSizeError, onSizeTypeChange }) => {
  const stockLevels = globalState?.metaobjects.stock_indicators?.sort((a, b) => b.max_quantity - a.max_quantity) || []
  const safeVariants = Array.isArray(variants) ? variants : []

  const hasKidsTag = result.tags && Array.isArray(result.tags) && result.tags.includes('Age:Kids')
  const defaultSizeRange = hasKidsTag ? 'UK' : (result.mfield_utopia_default_size_range || result.metafield_utopia_default_size_range || 'US')
  const normalizedDefaultSize = defaultSizeRange === 'EURO' ? 'EU' : defaultSizeRange

  const productKey = result.uid || result.id || result.handle
  const sizeTypeStorageKey = `__quickAddSizeType_${productKey}`

  const [selectedSizeType, setSelectedSizeType] = useState(() => {
    return window[sizeTypeStorageKey] || normalizedDefaultSize
  })

  const hasUniversalSizing = safeVariants.length > 0 && safeVariants.some(v => {
    if (!v || !v.id) {
      return false
    }
    const hasMeta = !!(v.mfield_products_uk_size || v.mfield_products_us_size || v.mfield_products_eu_sizing ||
           v.mfield_products_cm_sizing ||
           v.metafield_products_uk_size || v.metafield_products_us_size || v.metafield_products_eu_sizing ||
           v.metafield_products_cm_sizing ||
           (v.metafields && Array.isArray(v.metafields) && v.metafields.some(m =>
             m.namespace === 'products' && (m.key === 'uk_size' || m.key === 'us_size' || m.key === 'eu_sizing' || m.key === 'cm_sizing')
           )))
    return hasMeta
  })

  useEffect(() => {
    const storedSizeType = window[sizeTypeStorageKey]
    if (!storedSizeType && hasUniversalSizing) {
      setSelectedSizeType(normalizedDefaultSize)
      window[sizeTypeStorageKey] = normalizedDefaultSize
      if (onSizeTypeChange) {
        onSizeTypeChange(normalizedDefaultSize)
      }
    } else if (storedSizeType) {
      setSelectedSizeType(storedSizeType)
      if (onSizeTypeChange) {
        onSizeTypeChange(storedSizeType)
      }
    }
  }, [productKey])

  useEffect(() => {
    if (selectedSizeType) {
      window[sizeTypeStorageKey] = selectedSizeType
      if (onSizeTypeChange) {
        onSizeTypeChange(selectedSizeType)
      }
    }
  }, [selectedSizeType, onSizeTypeChange, sizeTypeStorageKey])

  const parseSizeValue = (sizeValue, sizeType) => {
    if (!sizeValue) return sizeValue

    if (!sizeValue.includes('-')) {
      return sizeValue
    }

    const regions = sizeValue.split('-').map(r => r.trim()).filter(r => r)

    for (const region of regions) {
      const cleanRegion = region.trim()

      if (sizeType === 'UK' && cleanRegion.startsWith('UK ')) {
        const ukContent = cleanRegion.replace('UK ', '').trim()
        if (ukContent.includes('/')) {
          return ukContent
        }
        return ukContent
      }

      if (sizeType === 'US' && cleanRegion.startsWith('US ')) {
        const usContent = cleanRegion.replace('US ', '').trim()
        if (usContent.includes('/')) {
          return usContent
        }
        return usContent
      }

      if (sizeType === 'EU' && cleanRegion.startsWith('EURO ')) {
        return cleanRegion.replace('EURO ', '').trim()
      }

      if (sizeType === 'CM' && cleanRegion.startsWith('CM ')) {
        return cleanRegion.replace('CM ', '').trim()
      }
    }

    return sizeValue
  }

  const getUniversalSizeValue = (variant, sizeType) => {
    if (!hasUniversalSizing || !variant) return null

    const getMeta = (key) => {
      return variant[`mfield_products_${key}`] ||
             variant[`metafield_products_${key}`] ||
             (variant.metafields && Array.isArray(variant.metafields) &&
              variant.metafields.find(m => m.namespace === 'products' && m.key === key)?.value) ||
             null
    }

    let rawSizeValue = null

    switch (sizeType) {
      case 'UK':
        rawSizeValue = getMeta('uk_size')
        break
      case 'US':
        rawSizeValue = getMeta('us_size')
        break
      case 'EU':
        rawSizeValue = getMeta('eu_sizing')
        break
      case 'CM':
        rawSizeValue = getMeta('cm_sizing')
        break
      default:
        return null
    }

    if (!rawSizeValue) return null

    return parseSizeValue(rawSizeValue, sizeType)
  }

  const getDisplaySize = (variant) => {
    if (!variant) return 'N/A'

    if (hasUniversalSizing) {
      const sizeValue = getUniversalSizeValue(variant, selectedSizeType)
      if (sizeValue && String(sizeValue).trim() !== '') {
        return String(sizeValue).trim()
      }
    }

    const fallbackValue = (variant.option2 && String(variant.option2).trim()) ||
                          (variant.option1 && String(variant.option1).trim()) ||
                          (variant.title && String(variant.title).trim()) ||
                          String(variant.id) ||
                          'N/A'

    if (fallbackValue && fallbackValue.includes('-') && hasUniversalSizing) {
      const parsedFallback = parseSizeValue(fallbackValue, selectedSizeType)
      if (parsedFallback && parsedFallback !== fallbackValue) {
        return parsedFallback
      }
    }

    return fallbackValue
  }

  const getStatusForSize = (size) => {
    const variant = safeVariants.find(v => {
      const vSize = v.option2 || v.option1 || v.title || String(v.id)
      return vSize === size
    })
    return getStockStatus(variant, stockLevels)
  }

  const handleSizeSelect = (variant) => {
    if (!variant || !variant.id) return
    const variantSize = variant.option2 || variant.option1 || variant.title || String(variant.id)
    setSelectedSize(variantSize)
  }

  const handleSizeGuideClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (sizeGuide?.id) {
      const modalId = `size-modal-${sizeGuide.id}`
      const modalElement = document.querySelector(`x-modal[data-id="${modalId}"]`)
      if (modalElement) {
        const openButton = modalElement.querySelector('[data-modal-role="open"]')
        if (openButton) {
          openButton.click()
        } else {
          const modalInstance = modalElement
          if (modalInstance && typeof modalInstance.handleShow === 'function') {
            modalInstance.handleShow()
          }
        }
      }
    }
  }

  return (
    <div className="w-full">
      <div className="flex flex-col">
        <div className="flex justify-between gap-x-1 relative mb-0.5 pr-[150px]">
          <x-modal data-id={`select-size-text-${result.id || result.uid}`} className="group/select-size-text flex items-center gap-x-0.25">
            <span className={`text-b font-semibold whitespace-nowrap text-ellipsis overflow-hidden transition-all ${showSizeError ? 'text-system-error' : 'group-data-visible/select-size-text:text-system-error'}`}>
              Select Size
            </span>
            <div className={`flex size-[16px] children:size-full text-system-error opacity-0 ${showSizeError ? 'opacity-100' : 'group-data-visible/select-size-text:opacity-100'} transition-all`}>
              <Icon id="attention" />
            </div>
          </x-modal>

          {(hasUniversalSizing || (sizeGuide && sizeGuide.tabs && Array.isArray(sizeGuide.tabs) && sizeGuide.tabs.length > 0)) && (
            <div className="flex items-center justify-end gap-x-0.625 absolute inset-tr md:gap-x-1 z-3">
              {hasUniversalSizing && (
                <div>
                  <x-modal
                    className="group/size-type-dropdown"
                    data-id={`size-type-dropdown-${result.id || result.uid}`}
                  >
                    <div className="flex flex-col relative">
                      <button
                        type="button"
                        className="btn btn-primary btn-text btn-s !gap-x-0.375 w-fit !h-fit !capitalize"
                        data-modal-parent={`size-type-dropdown-${result.id || result.uid}`}
                        data-modal-role="toggle"
                        aria-label="Size Type"
                      >
                        <span data-size-type-label>{selectedSizeType}</span>
                        <div className="w-[14px] h-[8px] children:size-full">
                          <Icon id="chevron-down" />
                        </div>
                      </button>
                      <div className="absolute inset-br w-fit h-fit min-w-[100px] translate-y-full bg-secondary border border-neutral-light opacity-0 group-data-visible/size-type-dropdown:opacity-100 invisible group-data-visible/size-type-dropdown:visible pointer-events-none group-data-visible/size-type-dropdown:pointer-events-auto transition-all z-3">
                        <ul className="flex flex-col">
                          {['US', 'UK', 'EU', 'CM'].map((sizeType) => (
                            <li key={sizeType}>
                              <button
                                type="button"
                                className={`w-full text-s px-1 py-0.5 link-fade ${selectedSizeType === sizeType ? 'font-semibold bg-primary text-secondary' : ''} transition-all hover:bg-neutral-light`}
                                data-size-type={sizeType}
                                data-modal-parent={`size-type-dropdown-${result.id || result.uid}`}
                                data-modal-role="close"
                                onClick={(e) => {
                                  e.preventDefault()
                                  e.stopPropagation()
                                  setSelectedSizeType(sizeType)
                                  if (onSizeTypeChange) {
                                    onSizeTypeChange(sizeType)
                                  }
                                }}
                                aria-label={`Show ${sizeType} sizes`}
                              >
                                {sizeType}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </x-modal>
                </div>
              )}
              {sizeGuide && sizeGuide.tabs && Array.isArray(sizeGuide.tabs) && sizeGuide.tabs.length > 0 && (
                <div className={`${hasUniversalSizing ? 'pl-0.625 md:pl-1 border-l border-neutral-medium' : ''}`}>
                  <button
                    type="button"
                    className="btn btn-primary btn-text btn-s w-fit !h-fit !capitalize"
                    data-modal-parent={`size-modal-${sizeGuide.id}`}
                    data-modal-role="open"
                    aria-label="Size Guide"
                    onClick={handleSizeGuideClick}
                  >
                    <span>Size Guide</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-0.5 relative">
          {safeVariants.map((variant) => {
            if (!variant || !variant.id) return null
            const variantSize = variant.option2 || variant.option1 || variant.title || String(variant.id) || 'Size'
            const status = getStatusForSize(variantSize) || { inStock: true, text: '', warning: false }
            const displaySize = getDisplaySize(variant)
            const isSelected = selectedSize === variantSize
            const canSelect = status && status.inStock !== false
            return (
              <div
                key={variant.id}
                role="button"
                aria-selected={isSelected}
                tabIndex={canSelect ? 0 : -1}
                className={`group/option grow w-full max-w-grid-1/4 lg:max-w-grid-1/6 !px-0 ${isSelected ? 'active' : ''}`}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  if (canSelect) {
                    handleSizeSelect(variant)
                  }
                }}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && canSelect) {
                    e.preventDefault()
                    handleSizeSelect(variant)
                  }
                }}
                aria-disabled={!canSelect}
                style={{ cursor: canSelect ? 'pointer' : 'not-allowed' }}
              >
                <div className={`btn btn-m !text-s ${canSelect ? 'btn-light btn-fill' : 'btn-light btn-light-alt btn-border pointer-events-none !cursor-not-allowed'} w-full !font-normal !px-0.25 !rounded-none group-[.active]/option:text-brand-white group-[.active]/option:bg-brand-black group-[.active]/option:border-brand-black`}>
                  <span className="overflow-hidden text-ellipsis whitespace-nowrap">{displaySize || variantSize || 'Size'}</span>
                </div>
                <span className="flex items-center gap-x-0.5 absolute -top-0.5 left-0 w-full max-w-variant-title text-b text-brand-black bg-secondary opacity-0 text-left group-[.active]/option:opacity-100 group-hover/option:opacity-100 whitespace-nowrap -translate-y-full overflow-hidden text-ellipsis pointer-events-none group-hover/option:z-2 transition-all">
                  <span className="font-semibold">Size </span>
                  <span>{displaySize}</span>
                  {status.text && (
                    <span
                      className="text-xs font-semibold uppercase"
                      style={{ color: status.textColour }}
                    >
                      {status.text}
                    </span>
                  )}
                </span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export function ResultQuickAdd({ result, firstPromoline, secondPromoline, sizeGuide }) {
  const buttonClass = 'btn btn-accent btn-fill btn-square w-[24px] lg:w-[36px]'

  const variants = Array.isArray(result.variants) ? result.variants : []
  const hasSingleVariant = variants.length === 1
  const singleVariant = hasSingleVariant ? variants[0] : null

  const handleAddToCart = async (variantToAdd = null) => {
    if (!variantToAdd) return

    if (window.Cart) {
      try {
        await window.Cart.addLineItems({
          id: variantToAdd.id,
          quantity: 1
        })
      } catch (error) {
        console.error('Error adding item to cart:', error)
      }
    }
  }

  const closeModal = (modalId) => {
    const modal = document.querySelector(`x-modal[data-id="${modalId}"]`)
    if (modal && typeof modal.handleHide === 'function') {
      modal.handleHide()
    }
  }

  const openModal = (modalId) => {
    setTimeout(() => {
      const modal = document.querySelector(`x-modal[data-id="${modalId}"]`)
      if (modal && typeof modal.handleToggle === 'function') {
        modal.handleToggle()
      }
    }, 100)
  }

  const handleModalOpen = () => {
    const modalId = 'quick-add'
    const quickAddContainer = document.querySelector('[data-quick-add-inner]')
    if (quickAddContainer) {
      const productKey = result.uid || result.id || result.handle
      render(
        <ModalContent
          key={productKey}
          result={result}
          modalId={modalId}
          firstPromoline={firstPromoline}
          secondPromoline={secondPromoline}
          sizeGuide={sizeGuide}
          onClose={() => closeModal(modalId)}
        />,
        quickAddContainer
      )
    }
    openModal(modalId)
  }

  if (hasSingleVariant) {
    const stockLevels = globalState?.metaobjects.stock_indicators?.sort((a, b) => b.max_quantity - a.max_quantity) || []
    const stockStatus = getStockStatus(singleVariant, stockLevels)
    const isDisabled = !stockStatus.inStock

    return (
      <button
        className={clsx(
          buttonClass,
          isDisabled && 'disabled'
        )}
        type="button"
        onClick={() => handleAddToCart(singleVariant)}
        disabled={isDisabled}
        aria-label="Add to cart"
      >
        <div className="flex size-[11px] children:size-full">
          <Icon id={'plus'} />
        </div>
      </button>
    )
  }

  return (
    <button
      className={buttonClass}
      type="button"
      onClick={handleModalOpen}
      aria-label="Open quick add modal"
    >
      <div className="flex size-[11px] children:size-full">
        <Icon id={'plus'} />
      </div>
    </button>
  )
}
